import { DetectedDate } from 'helpers/import-dates';
import {
  ImportableFile,
  ImportableFolder,
  ImportPlan,
  RunFile,
  RunTarget,
  UnsupportedFile,
} from 'helpers/import-types';

// State behind the Review Import screen, kept free of React so the rules
// (album date ranges, where unsorted files go, what gets imported) are
// testable on their own.

export const UNSORTED = 'unsorted';

export type ReviewFile<F extends ImportableFile = ImportableFile> = {
  key: string;
  item: F;
  included: boolean;
  detected: DetectedDate;
  /** Day picked by the user (YYYY-MM-DD); null means use the detected one. */
  override: string | null;
};

export type Destination =
  | { kind: 'unsorted' }
  | { kind: 'album'; albumKey: string }
  | { kind: 'existing'; albumId: number };

export type ReviewUnsortedFile<F extends ImportableFile = ImportableFile> =
  ReviewFile<F> & { destination: Destination };

export type DayRange = { start: string; end: string };

export type ReviewAlbum<
  F extends ImportableFile = ImportableFile,
  D extends ImportableFolder = ImportableFolder,
> = {
  key: string;
  folder: D;
  included: boolean;
  files: ReviewFile<F>[];
  /**
   * Files in the folder that can't be imported. Listed for the user to see,
   * but outside everything here: never selected, counted or imported.
   */
  unsupported: UnsupportedFile[];
  /** Range picked by the user; null follows the memories' dates. */
  rangeOverride: DayRange | null;
  /** Made on the review screen (not a subfolder) to hold unsorted files. */
  created?: boolean;
};

export type ReviewState<
  F extends ImportableFile = ImportableFile,
  D extends ImportableFolder = ImportableFolder,
> = {
  albums: ReviewAlbum<F, D>[];
  unsorted: ReviewUnsortedFile<F>[];
  /** Files directly in the chosen folder that can't be imported. */
  unsortedUnsupported: UnsupportedFile[];
};

export type ExistingAlbum = { id: number; name: string };

const naturally = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

const sortedUnsupported = (files: UnsupportedFile[] = []) =>
  [...files].sort((a, b) => naturally(a.name, b.name));

export const buildReviewState = <
  F extends ImportableFile,
  D extends ImportableFolder,
>(
  plan: ImportPlan<F, D>,
  detected: (file: F) => DetectedDate,
): ReviewState<F, D> => {
  const toReviewFile = (item: F): ReviewFile<F> => ({
    key: item.id,
    item,
    included: true,
    detected: detected(item),
    override: null,
  });
  return {
    albums: [...plan.albums]
      .sort((a, b) => naturally(a.folder.name, b.folder.name))
      .map((album) => ({
        key: `album:${album.folder.id}`,
        folder: album.folder,
        included: true,
        files: [...album.files]
          .sort((a, b) => naturally(a.name, b.name))
          .map(toReviewFile),
        unsupported: sortedUnsupported(album.unsupported),
        rangeOverride: null,
      })),
    unsorted: [...(plan.unsorted || [])]
      .sort((a, b) => naturally(a.name, b.name))
      .map((item) => ({
        ...toReviewFile(item),
        destination: { kind: 'unsorted' },
      })),
    unsortedUnsupported: sortedUnsupported(plan.unsortedUnsupported),
  };
};

export const fileDate = (file: ReviewFile) =>
  file.override ?? file.detected.date;

/** Where an unsorted file really goes: a switched-off album can't take it. */
export const resolveDestination = (
  state: ReviewState,
  destination: Destination,
  existingAlbums?: ExistingAlbum[],
): Destination => {
  if (destination.kind === 'album') {
    const album = state.albums.find((a) => a.key === destination.albumKey);
    if (!album || !album.included) return { kind: 'unsorted' };
  }
  if (
    destination.kind === 'existing' &&
    existingAlbums &&
    !existingAlbums.some((a) => a.id === destination.albumId)
  ) {
    return { kind: 'unsorted' };
  }
  return destination;
};

const sentTo = (file: ReviewUnsortedFile, albumKey: string) =>
  file.destination.kind === 'album' && file.destination.albumKey === albumKey;

/** Unsorted files the user sent to this import album, selected or not. */
export const assignedFiles = <F extends ImportableFile>(
  state: ReviewState<F>,
  albumKey: string,
): ReviewUnsortedFile<F>[] => {
  const album = state.albums.find((a) => a.key === albumKey);
  if (!album || !album.included) return [];
  return state.unsorted.filter((f) => sentTo(f, albumKey));
};

/** Every file headed into an import album: its own plus assigned unsorted. */
export const albumFiles = (state: ReviewState, albumKey: string) => {
  const album = state.albums.find((a) => a.key === albumKey);
  if (!album || !album.included) return [];
  return [
    ...album.files.filter((f) => f.included),
    ...assignedFiles(state, albumKey).filter((f) => f.included),
  ];
};

const sameName = (a: string, b: string) =>
  a.trim().localeCompare(b.trim(), undefined, { sensitivity: 'base' }) === 0;

/**
 * An album this import can already put files in under that name, so typing
 * a "new" album's name twice doesn't make two albums.
 */
export const findAlbumByName = (
  state: ReviewState,
  existingAlbums: ExistingAlbum[],
  name: string,
): Destination | null => {
  const inImport = state.albums.find(
    (a) => a.included && sameName(a.folder.name, name),
  );
  if (inImport) return { kind: 'album', albumKey: inImport.key };
  const inVault = existingAlbums.find((a) => sameName(a.name, name));
  if (inVault) return { kind: 'existing', albumId: inVault.id };
  return null;
};

const spanOf = (dates: (string | null)[]): DayRange | null => {
  const days = dates.filter((d): d is string => !!d).sort();
  return days.length > 0
    ? { start: days[0], end: days[days.length - 1] }
    : null;
};

/** The album's range as its memories' earliest and latest dates. */
export const albumAutoRange = (state: ReviewState, albumKey: string) =>
  spanOf(albumFiles(state, albumKey).map(fileDate));

export const albumRange = (state: ReviewState, album: ReviewAlbum) =>
  album.rangeOverride ?? albumAutoRange(state, album.key);

export type ReviewTotals = {
  memories: number;
  newAlbums: number;
  unsorted: number;
  toExistingAlbums: number;
  bytes: number;
  span: DayRange | null;
};

export const reviewTotals = (
  state: ReviewState,
  existingAlbums: ExistingAlbum[] = [],
): ReviewTotals => {
  let memories = 0;
  let newAlbums = 0;
  let bytes = 0;
  const dates: (string | null)[] = [];
  state.albums.forEach((album) => {
    const files = albumFiles(state, album.key);
    if (files.length === 0) return;
    newAlbums += 1;
  });
  const counted = (file: ReviewFile) => {
    memories += 1;
    bytes += file.item.size || 0;
    dates.push(fileDate(file));
  };
  state.albums.forEach(
    (a) => a.included && a.files.filter((f) => f.included).forEach(counted),
  );
  let unsorted = 0;
  let toExistingAlbums = 0;
  state.unsorted.forEach((file) => {
    if (!file.included) return;
    counted(file);
    const destination = resolveDestination(
      state,
      file.destination,
      existingAlbums,
    );
    if (destination.kind === 'unsorted') unsorted += 1;
    if (destination.kind === 'existing') toExistingAlbums += 1;
  });
  return {
    memories,
    newAlbums,
    unsorted,
    toExistingAlbums,
    bytes,
    span: spanOf(dates),
  };
};

/** Turns the review into what the importer runs, in order. */
export const buildRunTargets = <
  F extends ImportableFile,
  D extends ImportableFolder,
>(
  state: ReviewState<F, D>,
  existingAlbums: ExistingAlbum[],
): RunTarget<F>[] => {
  const toRun = (file: ReviewFile<F>): RunFile<F> => ({
    item: file.item,
    date: fileDate(file),
  });
  const targets: RunTarget<F>[] = [];

  state.albums.forEach((album) => {
    const files = albumFiles(state, album.key) as ReviewFile<F>[];
    if (files.length === 0) return;
    const range = albumRange(state, album);
    targets.push({
      kind: 'new',
      name: album.folder.name,
      startDate: range?.start ?? null,
      endDate: range?.end ?? null,
      files: files.map(toRun),
    });
  });

  const intoExisting = new Map<number, RunFile<F>[]>();
  const leftUnsorted: RunFile<F>[] = [];
  state.unsorted.forEach((file) => {
    if (!file.included) return;
    const destination = resolveDestination(
      state,
      file.destination,
      existingAlbums,
    );
    if (destination.kind === 'existing') {
      const list = intoExisting.get(destination.albumId) || [];
      list.push(toRun(file));
      intoExisting.set(destination.albumId, list);
    } else if (destination.kind === 'unsorted') {
      leftUnsorted.push(toRun(file));
    }
  });

  existingAlbums.forEach((album) => {
    const files = intoExisting.get(album.id);
    if (files && files.length > 0) {
      targets.push({
        kind: 'existing',
        albumId: album.id,
        name: album.name,
        files,
      });
    }
  });
  if (leftUnsorted.length > 0) {
    targets.push({ kind: 'unsorted', name: 'Unsorted', files: leftUnsorted });
  }
  return targets;
};

// ---------------------------------------------------------------------------
// Updates
// ---------------------------------------------------------------------------

export type ReviewAction =
  | { type: 'toggleAlbum'; albumKey: string }
  | { type: 'toggleFile'; scope: string; fileKey: string }
  | { type: 'setAllFiles'; scope: string; included: boolean }
  | { type: 'setFileDate'; scope: string; fileKey: string; date: string | null }
  | { type: 'setAllDates'; scope: string; date: string }
  | { type: 'setAlbumRange'; albumKey: string; range: DayRange | null }
  | { type: 'setDestination'; fileKey: string; destination: Destination }
  | { type: 'setAllDestinations'; destination: Destination }
  | { type: 'createAlbum'; albumKey: string; name: string; fileKeys: string[] }
  | { type: 'removeAlbum'; albumKey: string };

const withDate = <T extends ReviewFile>(file: T, date: string | null): T => ({
  ...file,
  override: date && date !== file.detected.date ? date : null,
});

// Applies a change to every file a card lists: the Unsorted card lists the
// unsorted files; an album card lists its own plus the unsorted sent to it.
const updateScope = <S extends ReviewState>(
  state: S,
  scope: string,
  update: <T extends ReviewFile>(file: T) => T,
): S => {
  if (scope === UNSORTED) {
    return { ...state, unsorted: state.unsorted.map(update) };
  }
  return {
    ...state,
    albums: state.albums.map((album) =>
      album.key === scope
        ? { ...album, files: album.files.map(update) }
        : album,
    ),
    unsorted: state.unsorted.map((f) => (sentTo(f, scope) ? update(f) : f)),
  };
};

export const reviewReducer = <S extends ReviewState>(
  state: S,
  action: ReviewAction,
): S => {
  switch (action.type) {
    case 'toggleAlbum':
      return {
        ...state,
        albums: state.albums.map((album) =>
          album.key === action.albumKey
            ? { ...album, included: !album.included }
            : album,
        ),
      };
    case 'toggleFile':
      return updateScope(state, action.scope, (f) =>
        f.key === action.fileKey ? { ...f, included: !f.included } : f,
      );
    case 'setAllFiles':
      return updateScope(state, action.scope, (f) => ({
        ...f,
        included: action.included,
      }));
    case 'setFileDate':
      return updateScope(state, action.scope, (f) =>
        f.key === action.fileKey ? withDate(f, action.date) : f,
      );
    case 'setAllDates':
      return updateScope(state, action.scope, (f) =>
        f.included ? withDate(f, action.date) : f,
      );
    case 'setAlbumRange':
      return {
        ...state,
        albums: state.albums.map((album) =>
          album.key === action.albumKey
            ? { ...album, rangeOverride: action.range }
            : album,
        ),
      };
    case 'setDestination':
      return {
        ...state,
        unsorted: state.unsorted.map((f) =>
          f.key === action.fileKey
            ? { ...f, destination: action.destination }
            : f,
        ),
      };
    case 'setAllDestinations':
      return {
        ...state,
        unsorted: state.unsorted.map((f) =>
          f.included ? { ...f, destination: action.destination } : f,
        ),
      };
    case 'createAlbum': {
      const album: ReviewAlbum = {
        key: action.albumKey,
        folder: { id: action.albumKey, name: action.name },
        included: true,
        files: [],
        unsupported: [],
        rangeOverride: null,
        created: true,
      };
      // Albums made here sit together at the top, in the order they were made.
      const firstFolder = state.albums.findIndex((a) => !a.created);
      const at = firstFolder === -1 ? state.albums.length : firstFolder;
      const destination: Destination = {
        kind: 'album',
        albumKey: action.albumKey,
      };
      const assign = new Set(action.fileKeys);
      return {
        ...state,
        albums: [
          ...state.albums.slice(0, at),
          album,
          ...state.albums.slice(at),
        ],
        unsorted: state.unsorted.map((f) =>
          assign.has(f.key) ? { ...f, destination } : f,
        ),
      };
    }
    case 'removeAlbum': {
      const album = state.albums.find((a) => a.key === action.albumKey);
      if (!album?.created) return state;
      return {
        ...state,
        albums: state.albums.filter((a) => a.key !== action.albumKey),
        unsorted: state.unsorted.map((f) =>
          sentTo(f, action.albumKey)
            ? { ...f, destination: { kind: 'unsorted' } }
            : f,
        ),
      };
    }
    default:
      return state;
  }
};
